/**
 * MKS-393 — MIRROR of `backend/src/constants/merchSizes.ts`.
 *
 * Everything from the first `export` below is byte-identical to that file, and
 * `backend/tests/merchSizeConstantsParity.test.ts` fails CI the moment the two
 * drift. Edit the backend copy and paste it here; do not edit only one.
 *
 * Why a mirror rather than the `shared/types/merch.ts` the ticket asked for:
 * `backend/Dockerfile` and `frontend/Dockerfile` are each built with their own
 * directory as the Docker build context (`docker-compose.production.yml:9`
 * and `:36`), so `../shared` exists in neither image. Nothing under
 * `shared/types/` is imported by `backend/src` or `frontend/src` today for
 * exactly that reason — the `@shared/*` alias in both tsconfigs is dead. A
 * genuinely shared module needs the build contexts widened first, which is a
 * change to protected infrastructure paths and a ticket of its own.
 */

/** T-shirt and hoodie scale, smallest first. */
export const MERCH_GARMENT_SIZES = [
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  '3XL',
  '4XL',
  '5XL',
] as const;

/**
 * Garment cut. Two values only: what a vendor prints as "unisex" is the `men`
 * block, so unisex maps to `men` rather than becoming a third value nobody can
 * order against.
 */
export const MERCH_CUTS = ['men', 'women'] as const;

/**
 * Sock sizes as the supplier sells them: S · M · L, not EU ranges.
 *
 * The scale was `35-38 · 39-42 · 43-46 · 47-50` until 2026-10-26;
 * `prisma/migrations/manual/20261026_002_mks466_sock_sizes.sql` clears every
 * stored range to NULL rather than guessing which letter it maps to — the
 * supplier's letters do not line up with the EU bands, so a mapping would
 * order the wrong size for somebody. The people affected re-pick against the
 * socks size chart, and the HR overview's "missing sizes" filter is what
 * surfaces them.
 */
export const MERCH_SOCK_SIZES = ['S', 'M', 'L'] as const;

/** Inclusive EU shoe-size bounds; only whole and half steps in between. */
export const MERCH_SHOE_SIZE_MIN = 35;
export const MERCH_SHOE_SIZE_MAX = 50;
export const MERCH_SHOE_SIZE_STEP = 0.5;

export type MerchGarmentSize = (typeof MERCH_GARMENT_SIZES)[number];
export type MerchCut = (typeof MERCH_CUTS)[number];
export type MerchSockSize = (typeof MERCH_SOCK_SIZES)[number];

/** Every selectable shoe size, as numbers: 35, 35.5, … 50. */
export const MERCH_SHOE_SIZES: readonly number[] = Array.from(
  { length: (MERCH_SHOE_SIZE_MAX - MERCH_SHOE_SIZE_MIN) / MERCH_SHOE_SIZE_STEP + 1 },
  (_, i) => MERCH_SHOE_SIZE_MIN + i * MERCH_SHOE_SIZE_STEP,
);

/** `35`, `35.5` — what the select option and the export cell read. */
export function formatShoeSize(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** In range AND on a half step. Rejects NaN/Infinity. */
export function isValidShoeSize(value: number): boolean {
  if (!Number.isFinite(value)) return false;
  if (value < MERCH_SHOE_SIZE_MIN || value > MERCH_SHOE_SIZE_MAX) return false;
  return Number.isInteger(value * 2);
}

/**
 * The six merch columns, in the order they appear everywhere a user sees them
 * (profile card, HR table, XLSX, CSV). Exported as one list so "all six are
 * empty" (the nudge banner's condition) and "which are missing" (the HR
 * overview's Missing column) are derived, never re-typed.
 */
export const MERCH_SIZE_FIELDS = [
  'merchTshirtSize',
  'merchTshirtCut',
  'merchHoodieSize',
  'merchHoodieCut',
  'merchSockSize',
  'merchShoeSize',
] as const;

export type MerchSizeField = (typeof MERCH_SIZE_FIELDS)[number];

/**
 * The caption of each field, used by the /profile selects, the HR table, the
 * XLSX sheet and the CSV alike, in field order.
 *
 * Shoes say `(EU)` because the bare number is ambiguous — 42 is a different
 * foot in UK and US sizing, and a swag order placed on the wrong system
 * arrives unwearable. Socks do not: the supplier sells them as S · M · L,
 * which belongs to no sizing system there is anything to disambiguate from.
 */
export const MERCH_FIELD_LABELS: Record<MerchSizeField, string> = {
  merchTshirtSize: 'T-shirt size',
  merchTshirtCut: 'T-shirt cut',
  merchHoodieSize: 'Hoodie size',
  merchHoodieCut: 'Hoodie cut',
  merchSockSize: 'Socks size',
  merchShoeSize: 'Shoe size (EU)',
};

/** A person's six merch values; `null` means "not filled in". */
export interface MerchSizes {
  merchTshirtSize: MerchGarmentSize | null;
  merchTshirtCut: MerchCut | null;
  merchHoodieSize: MerchGarmentSize | null;
  merchHoodieCut: MerchCut | null;
  merchSockSize: MerchSockSize | null;
  /** EU size as a number (41, 41.5); `Decimal` on the wire is serialized to this. */
  merchShoeSize: number | null;
}

/** True when not one of the six is set — the nudge banner's condition. */
export function hasNoMerchSizes(sizes: Partial<MerchSizes> | null | undefined): boolean {
  if (!sizes) return true;
  return MERCH_SIZE_FIELDS.every((field) => sizes[field] === null || sizes[field] === undefined);
}

/** The fields this person has not filled in, in display order. */
export function missingMerchFields(sizes: Partial<MerchSizes>): MerchSizeField[] {
  return MERCH_SIZE_FIELDS.filter(
    (field) => sizes[field] === null || sizes[field] === undefined,
  );
}
